import { useState, useEffect } from 'react';
import Reveal from '@/components/base/Reveal';
import SmartLink from '@/components/base/SmartLink';
import Logo from '@/components/base/Logo';
import Footer from '@/pages/home/components/Footer';

function ScrollNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Our Mission', href: '/mission' },
    { label: 'What We Do', href: '/projects' },
    { label: 'About Us', href: '/about' },
  ];

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/90 backdrop-blur-md border-b border-white/5' : ''
      }`}
    >
      <div className="px-6 md:px-12 lg:px-16 pt-6 pb-4">
        <nav
          className={`rounded-xl px-4 py-2 flex items-center justify-between transition-all duration-300 ${
            scrolled ? 'bg-transparent' : 'liquid-glass'
          }`}
        >
          <SmartLink href="/">
            <Logo className="h-5 sm:h-6 md:h-7 w-auto text-white shrink-0" />
          </SmartLink>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <SmartLink
                key={link.label}
                href={link.href}
                className={`text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                  link.href === '/projects' ? 'text-white' : 'text-white/80 hover:text-gray-300'
                }`}
              >
                {link.label}
              </SmartLink>
            ))}
          </div>
          <a href="mailto:hello@sapphiremedia.group">
            <button className="bg-white text-black px-3 py-1.5 sm:px-5 sm:py-2 md:px-6 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-1 sm:gap-1.5">
              Reach out
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="size-3.5 sm:size-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </button>
          </a>
        </nav>
      </div>
    </div>
  );
}

type Project = {
  number: string;
  title: string;
  category: string;
  body: string;
  status: 'Live' | 'Building' | 'Planning';
  href?: string;
  external?: boolean;
};

const projects: Project[] = [
  {
    number: '01',
    title: 'Sapphire Radio',
    category: 'On-air · Live',
    body: 'A 24/7 internet radio station programmed by young hosts. Talk shows, music blocks, and live interviews — all built on open-source broadcast infrastructure we maintain ourselves.',
    status: 'Live',
    href: 'https://sapphire.radio',
    external: true,
  },
  {
    number: '02',
    title: 'Sapphy Bot',
    category: 'Software · Closed source',
    body: 'A (paid) Discord bot for community moderation, automation, and on-air integrations. Free for any non-profit to self-host (contact us). Written in TypeScript.',
    status: 'Building',
    // href: 'https://store.sapphiremedia.group/product/sapphy-bot',
    // external: true,
  },
  {
    number: '03',
    title: 'InstantOS',
    category: 'Software · Open source',
    body: 'Youth working on an operating system. Built in C++, they’re working on one of their biggest projects to date.',
    status: 'Building',
    href: 'https://github.com/sapphiremediagroup/InstantOS'
  },
  {
    number: '04',
    title: 'Open Curriculum',
    category: 'Education · Open source',
    body: 'An open learning platform. Youth can contribute guides, tips and tricks, and more. The place to share and learn, all by youth volunteers.',
    status: 'Planning',
  },
];

const ways = [
  {
    icon: 'ri-code-s-slash-line',
    title: 'Build with us',
    body: 'Pick up an issue on GitHub, ship your first PR, or propose a new tool. We pair new engineers with mentors.',
    cta: 'See open issues',
    href: 'https://github.com/sapphiremediagroup',
  },
  {
    icon: 'ri-mic-line',
    title: 'Host a show',
    body: "Pitch us a 30-minute radio block on anything you care about — music, interviews, late-night rambling. We'll get you on the air.",
    cta: 'Pitch a show',
    href: 'mailto:hello@sapphiremedia.group?subject=Show%20pitch',
  },
  {
    icon: 'ri-quill-pen-line',
    title: 'Tell a story',
    body: 'Write, report, or produce a piece for Sapphire Radio. Edited by working journalists, published with your byline.',
    cta: 'Pitch a story',
    href: 'mailto:hello@sapphiremedia.group?subject=Story%20pitch',
  },
  {
    icon: 'ri-heart-line',
    title: 'Sponsor or donate',
    body: 'Tax-deductible support for hosting, equipment, and stipends. Every dollar goes directly into the work.',
    cta: 'Donate',
    href: 'https://hcb.hackclub.com/donations/start/sapphire',
  },
];

const statusStyles: Record<Project['status'], string> = {
  Live: 'text-emerald-300/80 border-emerald-300/20 bg-emerald-300/[0.03]',
  Building: 'text-amber-200/80 border-amber-200/20 bg-amber-200/[0.03]',
  Planning: 'text-white/50 border-white/15 bg-white/[0.02]',
};

export default function ProjectsPage() {
  return (
    <div className="bg-black min-h-screen">
      <ScrollNavbar />

      {/* 1 — Hero */}
      <section className="relative pt-44 pb-20 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-2 lg:gap-20 lg:items-end">
          <div>
            <Reveal variant="fadeUp" duration={800}>
              <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-6">What We Do</p>
            </Reveal>
            <Reveal variant="fadeUp" delay={100} duration={900}>
              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-normal text-white font-serif"
                style={{ letterSpacing: '-0.02em' }}
              >
                Software, stories,<br />and shows.
              </h1>
            </Reveal>
          </div>
          <Reveal variant="fadeUp" delay={240} duration={800}>
            <p className="text-base text-white/55 leading-relaxed mt-10 lg:mt-0 max-w-md">
              Everything Sapphire ships lives in one of three buckets: open-source software, editorial work, and live radio. Here's what's running now — and how you can plug in.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 2 — Projects grid */}
      <section className="px-6 md:px-12 lg:px-16 pb-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            {projects.map((project, i) => {
              const card = (
                <div className="bg-black p-10 md:p-12 group hover:bg-white/[0.02] transition-colors duration-300 h-full flex flex-col">
                  <div className="flex items-start justify-between mb-8">
                    <span className="text-xs text-white/45 font-mono">{project.number}</span>
                    <span
                      className={`text-[10px] tracking-wide uppercase px-2.5 py-1 rounded-full border ${statusStyles[project.status]}`}
                    >
                      {project.status}
                    </span>
                  </div>
                  <p className="text-[11px] text-white/40 tracking-wide uppercase mb-3">{project.category}</p>
                  <h3
                    className="text-2xl md:text-3xl font-normal text-white font-serif mb-4"
                    style={{ letterSpacing: '-0.02em' }}
                  >
                    {project.title}
                  </h3>
                  <p className="text-sm text-white/60 leading-relaxed mb-8 flex-1">{project.body}</p>
                  {project.href && (
                    <span className="text-xs text-white/70 inline-flex items-center gap-1.5 group-hover:text-white transition-colors duration-200">
                      Visit
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="size-3.5">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
                      </svg>
                    </span>
                  )}
                </div>
              );

              return (
                <Reveal key={project.number} variant="fadeUp" delay={i * 100} duration={800} threshold={0.1}>
                  {project.href ? (
                    <a
                      href={project.href}
                      {...(project.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                      className="block h-full cursor-pointer"
                    >
                      {card}
                    </a>
                  ) : (
                    card
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 3 — Ways to get involved */}
      <section className="py-24 px-6 md:px-12 lg:px-16 bg-[#080808] border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Reveal variant="fadeUp" duration={700}>
            <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-4">Get involved</p>
          </Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <Reveal variant="fadeUp" delay={80} duration={900}>
              <h2 className="text-4xl md:text-5xl font-normal text-white font-serif" style={{ letterSpacing: '-0.02em' }}>
                Four ways<br />to plug in.
              </h2>
            </Reveal>
            <Reveal variant="fadeUp" delay={160} duration={800}>
              <p className="text-sm text-white/50 max-w-xs leading-relaxed">
                Bring whatever you have — code, a voice, a story, or a few dollars. We'll find the right place for it.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            {ways.map((way, i) => (
              <Reveal key={way.title} variant="fadeUp" delay={i * 100} duration={800} threshold={0.1}>
                <a
                  href={way.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block bg-[#080808] p-10 group hover:bg-white/[0.025] transition-colors duration-300 h-full"
                >
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg border border-white/10 text-white/50 group-hover:border-white/25 group-hover:text-white/80 transition-all duration-200 mb-6">
                    <i className={`${way.icon} text-base`} />
                  </div>
                  <h3 className="text-lg font-medium text-white mb-3">{way.title}</h3>
                  <p className="text-sm text-white/55 leading-relaxed mb-6">{way.body}</p>
                  <span className="text-xs text-white/70 inline-flex items-center gap-1.5 group-hover:text-white transition-colors duration-200">
                    {way.cta}
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="size-3.5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                    </svg>
                  </span>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 4 — CTA */}
      <section className="py-28 px-6 md:px-12 lg:px-16 bg-black border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal variant="fadeUp" duration={900}>
            <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-6">Build with us</p>
          </Reveal>
          <Reveal variant="fadeUp" delay={120} duration={950}>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-normal text-white mb-8 font-serif"
              style={{ letterSpacing: '-0.02em' }}
            >
              The next show.<br />The next repo. Yours.
            </h2>
          </Reveal>
          <Reveal variant="scaleUp" delay={260} duration={800}>
            <a
              href="https://discord.gg/K2zNSVTT9e"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block bg-white text-black px-10 py-4 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
            >
              Join the Discord
            </a>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
